// PrivacyGate — the full-page privacy + copyright consent step, shown right
// after sign-in and before any use of the app (owner decision 2026-09-29,
// "Full-page gate", refined 2026-10-06: GDPR and copyright are the first
// thing that happens; once inside, work is never interrupted by such notices).
//
// Layout: every data point is a bullet, never prose. NECESSARY processing
// (no choice: the app cannot work without it) is separated from OPTIONAL
// processing (real toggles, never pre-ticked). The one primary action is
// inert until the Author performs two explicit affirmative acts (read the
// terms; confirms the right to use what they upload) — consent is never
// inferred from use (GDPR Art. 7). Declining is always available.
//
// The copy is HONEST about erasure (dogfood D21/D22): removing a single item
// hides it but keeps it in the history; deleting the account removes the
// workspace and its history. Bump `currentPrivacyTermsVersion` in
// `@bilda/app` whenever this text changes materially — everyone is asked
// again.
//
// Pure and props-driven: `state` is the single source of truth, every toggle
// and action reports UP. The host (`app/AppShellHost/PrivacyGateHost.tsx`)
// owns the form draft and the command dispatch.

export type { PrivacyGateState };
export { PrivacyGate };

import { Trans, useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Button } from "../Button/Button.js";
import { Checkbox } from "../Checkbox/Checkbox.js";
import { Heading } from "../Heading/Heading.js";
import { Icon, type IconName } from "../Icon/Icon.js";
import { InlineError } from "../InlineError/InlineError.js";
import { PublicBrand } from "../PublicBrand/PublicBrand.js";
import { Switch } from "../Switch/Switch.js";
import { Text } from "../Text/Text.js";
import "./PrivacyGate.css";

type PrivacyGateStatus =
    | { readonly _tag: "editing" }
    | { readonly _tag: "submitting" }
    | { readonly _tag: "failed" };

type PrivacyGateState = {
    // Required affirmative acts — both must be ticked to continue.
    readonly termsRead: boolean;
    readonly rightsConfirmed: boolean;
    // Optional: the AI activity summary (`SetSummaryConsent`). Off by default.
    readonly summaryOptIn: boolean;
    readonly status: PrivacyGateStatus;
};

type PrivacyGateProps = {
    readonly state: PrivacyGateState;
    readonly onTermsReadChange: (checked: boolean) => void;
    readonly onRightsConfirmedChange: (checked: boolean) => void;
    readonly onSummaryOptInChange: (checked: boolean) => void;
    readonly onAccept: () => void;
    readonly onDecline: () => void;
};

const Section = ({
    icon,
    title,
    children,
}: {
    readonly icon: IconName;
    readonly title: ReactNode;
    readonly children: ReactNode;
}) => (
    <section className="bilda-privacy-gate__section">
        <span className="bilda-privacy-gate__section-icon" aria-hidden="true">
            <Icon name={icon} size="md" />
        </span>
        <div className="bilda-privacy-gate__section-body">
            <Heading level={2} size="lg">
                {title}
            </Heading>
            <ul className="bilda-privacy-gate__list">{children}</ul>
        </div>
    </section>
);

const SUMMARY_SWITCH_ID = "privacy-gate-summary";

const PrivacyGate = ({
    state,
    onTermsReadChange,
    onRightsConfirmedChange,
    onSummaryOptInChange,
    onAccept,
    onDecline,
}: PrivacyGateProps) => {
    const { t } = useLingui();
    const submitting = state.status._tag === "submitting";
    const canAccept = state.termsRead && state.rightsConfirmed && !submitting;
    return (
        <main className="bilda-privacy-gate" data-theme="light">
            {/* The public brand band (redesign A-03): the gate sits between
                sign-in and the app, so it wears the public look. */}
            <div className="bilda-privacy-gate__band">
                <PublicBrand />
            </div>
            <div className="bilda-privacy-gate__card">
                <header className="bilda-privacy-gate__header">
                    <h1 className="bilda-privacy-gate__title">
                        <Trans>Before you start</Trans>
                    </h1>
                    <Text tone="dim" as="p">
                        <Trans>
                            How the app handles your data and the material you
                            upload. You only need to read this once.
                        </Trans>
                    </Text>
                </header>

                <Section
                    icon="database"
                    title={t`What we store (needed for the app to work)`}
                >
                    <li>
                        <Trans>Your email address, to sign you in</Trans>
                    </li>
                    <li>
                        <Trans>
                            The names you enter (your name, your workspace's
                            name)
                        </Trans>
                    </li>
                    <li>
                        <Trans>
                            What you upload and create: sources, materials,
                            chats and journal entries
                        </Trans>
                    </li>
                    <li>
                        <Trans>
                            A history of your changes, so you can see and undo
                            them
                        </Trans>
                    </li>
                    <li>
                        <Trans>A cookie that keeps you signed in</Trans>
                    </li>
                </Section>

                <Section icon="person" title={t`Who else handles it`}>
                    <li>
                        <Trans>
                            Anthropic (AI provider): the text of your sources,
                            materials and chat messages is sent to Anthropic's
                            AI so the assistant can answer and write
                        </Trans>
                    </li>
                    <li>
                        <Trans>Auth0: your email address, to sign you in</Trans>
                    </li>
                    <li>
                        <Trans>
                            Our hosting provider: runs the server and stores the
                            database
                        </Trans>
                    </li>
                </Section>

                <Section icon="clock" title={t`How long we keep it`}>
                    <li>
                        <Trans>
                            Everything is kept until you delete your account.
                        </Trans>
                    </li>
                    <li>
                        <Trans>
                            Removing a single item, such as a source, hides it,
                            but it stays in the history. Erasing single items
                            completely is not possible yet.
                        </Trans>
                    </li>
                    <li>
                        <Trans>
                            Under Settings → Workspace you can download all your
                            data and delete your account. Deleting the account
                            removes your email, your workspace and its history,
                            the files you uploaded and the records of your AI
                            use. We keep only an anonymous note that an account
                            was deleted, and when. Auth0 keeps its own sign-in
                            record of your email.
                        </Trans>
                    </li>
                    <li>
                        <Trans>
                            What you add to a workspace someone else shared with
                            you stays in that workspace.
                        </Trans>
                    </li>
                </Section>

                <Section icon="file" title={t`Your material`}>
                    <li>
                        <Trans>
                            Only upload material you have the right to use, for
                            example your own texts or material your school has a
                            licence for.
                        </Trans>
                    </li>
                    <li>
                        <Trans>
                            Avoid uploading personal data about others, such as
                            students' names and grades.
                        </Trans>
                    </li>
                </Section>

                <section className="bilda-privacy-gate__optional">
                    <Heading level={2} size="lg">
                        <Trans>Optional</Trans>
                    </Heading>
                    <div className="bilda-privacy-gate__option">
                        <div className="bilda-privacy-gate__option-text">
                            <Text as="label" htmlFor={SUMMARY_SWITCH_ID}>
                                <Trans>Summarize my activity</Trans>
                            </Text>
                            <Text tone="dim" size="sm" as="p">
                                <Trans>
                                    The assistant writes short summaries of what
                                    you do in the app, so it can pick up where
                                    you left off. Written by Anthropic's AI. Off
                                    unless you turn it on; change it any time
                                    under Settings → Assistant.
                                </Trans>
                            </Text>
                        </div>
                        <Switch
                            checked={state.summaryOptIn}
                            onCheckedChange={onSummaryOptInChange}
                            label={t`Summarize my activity`}
                            disabled={submitting}
                        />
                    </div>
                </section>

                <section className="bilda-privacy-gate__consent">
                    <Checkbox
                        id="privacy-gate-terms-read"
                        checked={state.termsRead}
                        onCheckedChange={onTermsReadChange}
                        disabled={submitting}
                        label={
                            <Text as="span">
                                <Trans>
                                    I have read how my data is handled.
                                </Trans>
                            </Text>
                        }
                    />
                    <Checkbox
                        id="privacy-gate-rights-confirmed"
                        checked={state.rightsConfirmed}
                        onCheckedChange={onRightsConfirmedChange}
                        disabled={submitting}
                        label={
                            <Text as="span">
                                <Trans>
                                    I only upload material I have the right to
                                    use.
                                </Trans>
                            </Text>
                        }
                    />
                    {state.status._tag === "failed" ? (
                        <InlineError>
                            <Trans>
                                Your answer could not be saved. Try again.
                            </Trans>
                        </InlineError>
                    ) : null}
                    <div className="bilda-privacy-gate__actions">
                        <Button
                            variant="primary"
                            disabled={!canAccept}
                            onClick={onAccept}
                        >
                            {submitting ? t`Saving…` : t`Accept and continue`}
                        </Button>
                        <Button
                            variant="ghost"
                            disabled={submitting}
                            onClick={onDecline}
                        >
                            <Trans>Decline and sign out</Trans>
                        </Button>
                    </div>
                </section>
            </div>
        </main>
    );
};
